import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { to, toWithSender } from '../lib/nav'

import {
  type ActionResult,
  ApiError,
  type ReportActions,
  type StoredPdfInfo,
  apiFetch,
  completeStudy,
  esignReport,
  fetchStoredPdf,
  getReportActions,
  getStoredPdfInfo,
  refreshReport,
} from '../lib/api'
import ReportDocument, { type RenderModel } from '../components/ReportDocument'

// Study Report hub.
//
// Behavioural specification: `oldcode/StudyReport.aspx(.vb)`.
//
// The legacy hub carries seven buttons. Two of them route: `btnEdit`
// ("Edit Measurements", :358-393) switches on study type and opens that
// modality's worksheet, and `btnInterpret` ("Edit Interpretation", :396-412)
// opens the structured interpretation form — which exists for only four types,
// Echo, TEE, Carotid and Vascular. General is the odd one out: it has no
// `btnEdit` case at all, and reaches its worksheet through `btnInterpret`
// instead (:409-410).
//
// Three write: E-Sign (:526), Mark Completed (:520) and Refresh Report (:567).
// They are here, each behind a confirmation that says what it will do, because
// none of them is undoable from this screen. What actually changed is shown
// afterwards — the legacy simply re-rendered the page, so a user had no way to
// tell a signature that landed from one that silently did not.
//
// Send Report and Delete are still absent by instruction: faxing is data
// capture only, and Delete is out of scope.

interface Report {
  report_id: number | null
  table: string | null
  referenced_file: string | null
  electronically_signed: boolean
  dictated_not_read: boolean
  both_captions: boolean
  reading_physician: string | null
  dictation_date: string | null
  transcription_date: string | null
  interpretation: string | null
  note: string | null
}

// `GET /studies/{id}/open` resolves the study's type and, with it, which
// worksheet and interpretation form the legacy would have opened.
interface OpenTarget {
  kind: string
  path: string
  reason: string
  study_type: string | null
  worksheet: string | null
  interpretation: string | null
  /** Set where the report content is authored in another application — DEC-019.
   *  `worksheet` and `interpretation` are null whenever this is set. */
  report_content_read_only: string | null
}

// `12319999` and `9999-12-31` are the legacy "not dictated / not transcribed"
// sentinel, on 357,187 rows. Shown as what it means rather than as a date in
// the year 9999.
const SENTINEL = /12319999|9999-12-31/

type Pending = 'esign' | 'complete' | 'refresh'

const CAPTION: Record<Pending, string> = {
  esign: 'E-Sign',
  complete: 'Mark Completed',
  refresh: 'Refresh Report',
}

export default function StudyReportPage() {
  const { studyId } = useParams()
  const [params] = useSearchParams()
  const [report, setReport] = useState<Report | null>(null)
  const [open, setOpen] = useState<OpenTarget | null>(null)
  const [error, setError] = useState<string | null>(null)

  // `ReportViewer.aspx`: the PDF the legacy exported over `ReferencedFile`.
  // Read separately from the report summary because the summary only reads
  // `tblStudyReport` — a LEA or ABI study keeps its filename in its own table.
  const [pdf, setPdf] = useState<StoredPdfInfo | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)

  // The report document, drawn on the hub itself.
  //
  // `StudyReport.aspx` was never a menu of buttons: it showed the exported PDF
  // with the buttons around it, and the buttons act on the thing you are
  // looking at. This is the same `ReportDocument` the standalone view uses and
  // the same model `document_html` prints, so what is on this page is what
  // comes out of Download PDF.
  const [document_, setDocument] = useState<RenderModel | null>(null)
  const [documentError, setDocumentError] = useState<string | null>(null)

  const [actions, setActions] = useState<ReportActions | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [result, setResult] = useState<{ kind: Pending; data: ActionResult } | null>(null)

  const loadReport = useCallback(
    () =>
      apiFetch<Report>(`/api/v1/studies/${studyId}/report`)
        .then(setReport)
        .catch((e) => setError(e instanceof ApiError ? e.message : String(e))),
    [studyId],
  )

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  // A failure here must not blank the hub: the report itself is still useful
  // without the routing, so this one is reported inline instead.
  useEffect(() => {
    setOpen(null)
    apiFetch<OpenTarget>(`/api/v1/studies/${studyId}/open`)
      .then(setOpen)
      .catch(() => setOpen(null))
  }, [studyId])

  // Whether there is a stored PDF at all, and whether the share can be reached.
  // A failure here leaves the link off rather than blanking the hub.
  useEffect(() => {
    setPdf(null)
    getStoredPdfInfo(Number(studyId))
      .then(setPdf)
      .catch(() => setPdf(null))
  }, [studyId])

  // A failure here must not blank the hub: the buttons still work on a study
  // whose type has no template, and the reason is shown where the report would
  // have been.
  useEffect(() => {
    setDocument(null)
    setDocumentError(null)
    apiFetch<RenderModel>(`/api/v1/studies/${studyId}/report/render`)
      .then(setDocument)
      .catch((e) => setDocumentError(e instanceof ApiError ? e.message : String(e)))
  }, [studyId])

  // The blob URL is this document's, so it has to be handed back.
  useEffect(() => {
    setPdfUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous)
      return null
    })
    setPdfError(null)
  }, [studyId])
  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl)
  }, [pdfUrl])

  async function openStoredPdf() {
    setPdfBusy(true)
    setPdfError(null)
    try {
      const url = await fetchStoredPdf(Number(studyId))
      setPdfUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous)
        return url
      })
    } catch (e) {
      // The 503 is the one that matters: the share is not mounted here, which
      // is an operations problem and not a missing report. Say it verbatim.
      setPdfError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setPdfBusy(false)
    }
  }

  const loadActions = useCallback(
    () =>
      getReportActions(Number(studyId))
        .then(setActions)
        .catch(() => setActions(null)),
    [studyId],
  )

  useEffect(() => {
    setActions(null)
    setPending(null)
    setResult(null)
    setActionError(null)
    void loadActions()
  }, [loadActions])

  function begin(kind: Pending) {
    setPending(kind)
    setPin('')
    setResult(null)
    setActionError(null)
  }

  async function run(kind: Pending) {
    const id = Number(studyId)
    setBusy(true)
    setActionError(null)
    try {
      const data =
        kind === 'esign'
          ? await esignReport(id, pin.trim() || null)
          : kind === 'complete'
            ? await completeStudy(id)
            : await refreshReport(id)
      setResult({ kind, data })
      setPending(null)
      setPin('')
      // The write is committed; a failed re-read must not read as a failure.
      await Promise.all([loadReport(), loadActions()]).catch(() => undefined)
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const back = to('/', params)
  const self = to(`/studies/${studyId}/report`, params)

  // `StudyReport.aspx.vb:371-391` — every one of btnEdit's fourteen redirects
  // appends `&SENDER=StudyReport.aspx?STUDYID=<id>`, so the worksheet's Generate
  // and Cancel come back here rather than dropping the user a level.
  const worksheet = open?.worksheet
    ? toWithSender(`/studies/${studyId}/worksheet/${open.worksheet}`, params, self)
    : null
  // `btnEdit` has no `Case Global.StudyType.General`; the General worksheet
  // hangs off `btnInterpret` instead, so it is captioned as that button.
  const worksheetCaption =
    open?.study_type === 'General' ? 'Edit Interpretation' : 'Edit Measurements'

  if (error) {
    return (
      <main className="page">
        <p className="banner banner-error">{error}</p>
        <Link to={back}>Back to worklist</Link>
      </main>
    )
  }
  if (!report) return <main className="page"><p className="muted">Loading…</p></main>

  return (
    <main className="page page-wide">
      <PageNav strip="studyReport" studyId={studyId} />

      <div className="page-head">
        <h1>Study Report</h1>
        <p className="subtle">
          {open?.study_type ? `${open.study_type} study` : 'Study'} {studyId}
        </p>
      </div>

      {/* The two routing buttons from the legacy button row, with their legacy
          captions. Neither writes: the worksheet is read-only here. */}
      <div className="actions">
        {!open && <span className="muted">Resolving study type…</span>}
        {open &&
          (worksheet ? (
            <Link className="link" to={worksheet}>{worksheetCaption}</Link>
          ) : open.report_content_read_only ? (
            // Not "the legacy has no worksheet for Echo" — it has one, and
            // saying otherwise sends the next reader to look for a bug. The
            // report is authored in another application (DEC-019).
            <span className="muted">{open.report_content_read_only}</span>
          ) : (
            <span className="muted">
              {worksheetCaption} — the legacy has no worksheet for{' '}
              {open.study_type ?? 'this study type'}.
            </span>
          ))}
        {open?.interpretation && (
          <span className="muted">
            Edit Interpretation — the structured interpretation form is not built yet.
          </span>
        )}
      </div>

      {/* The three writing buttons, in the legacy's own order and with its own
          captions. Each is drawn only when the legacy would have drawn it, and
          disabled with the reason when it would not — the server refuses the
          same cases regardless, so this is courtesy rather than the gate. */}
      <div className="actions">
        <ActionButton
          caption={CAPTION.esign}
          allowed={actions?.can_esign ?? false}
          reason={actions?.esign_reason ?? null}
          loading={!actions}
          disabled={busy || pending !== null}
          onClick={() => begin('esign')}
        />
        <ActionButton
          caption={CAPTION.complete}
          allowed={actions?.can_mark_completed ?? false}
          reason={actions?.mark_completed_reason ?? null}
          loading={!actions}
          disabled={busy || pending !== null || (actions?.already_completed ?? false)}
          onClick={() => begin('complete')}
        />
        <ActionButton
          caption={CAPTION.refresh}
          allowed={actions?.refresh_available ?? false}
          reason={actions?.refresh_reason ?? null}
          loading={!actions}
          disabled={busy || pending !== null}
          onClick={() => begin('refresh')}
        />
        {/* `cmdOpenViewer` — the last button in the legacy row, which opened
            the WebViewer at 10.1.230.10 in a popup
            (`StudyReport.aspx.vb:1153-1160`). The images live here now, so it is
            a link rather than a `window.open`, but it keeps its caption and its
            place at the end of the row. */}
        <Link className="link" to={to(`/studies/${studyId}/images`, params)}>
          View Online
        </Link>
        {actions?.already_signed && (
          <span className="muted">Already electronically signed.</span>
        )}
        {actions?.already_completed && <span className="muted">Already Completed.</span>}
      </div>
      <p className="hint muted">
        Send Report is data capture only and Delete is out of scope; the worksheet
        opens read-only.
      </p>

      {pending && actions && (
        <Confirm
          kind={pending}
          actions={actions}
          pin={pin}
          onPin={setPin}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => void run(pending)}
        />
      )}

      {actionError && <p className="banner banner-error">{actionError}</p>}
      {result && <Outcome kind={result.kind} data={result.data} />}

      {/* The report. `StudyReport.aspx` showed the exported PDF here; this is
          the same document rendered from the same model the PDF is printed
          from, so the page and the file cannot disagree. */}
      <fieldset className="report-on-hub">
        <legend>Report</legend>
        {documentError && (
          <p className="banner banner-warn">{documentError}</p>
        )}
        {!document_ && !documentError && <p className="muted">Rendering…</p>}
        {document_ && (
          <>
            {/* No live-render banner here, by product direction (14 August
                2026). The Stored PDF fieldset further down still names the
                report of record when there is one.

                What this removed: a study reported outside this system — echo
                and TEE both are, per `docs/BACKLOG.md` "Echo is a workflow
                shell" — draws as the template with demographics and no
                findings, and now does so unannounced. */}
            <p className="subtle">
              Template <code>{document_.template}</code>
              {(document_.page_count ?? 1) > 1 && ` · ${document_.page_count} pages`}
              {' · '}
              <Link to={`/studies/${studyId}/report/view?${params.toString()}`}>
                open on its own
              </Link>
            </p>
            <div className="report-scroll">
              <ReportDocument model={document_} />
            </div>
          </>
        )}
      </fieldset>

      {/* `ReportViewer.aspx`. Offered only when a filename is stored, because
          that page has nothing to serve without one — the legacy would have
          written the folder path and stopped. Whether the share is reachable is
          a separate question, answered when the link is followed. */}
      {pdf?.has_stored_pdf && (
        <fieldset>
          <legend>Stored PDF</legend>
          <div className="ro-field">
            <span className="ro-label">File</span>
            <span className="ro-value mono">{pdf.referenced_file}</span>
            <span className="ro-note">
              <code>
                {pdf.table}.{pdf.key} = {pdf.key_value}
              </code>
              , served from the reports folder. The legacy took this name from
              the query string instead.
            </span>
          </div>
          <div className="actions">
            <button type="button" disabled={pdfBusy} onClick={() => void openStoredPdf()}>
              {pdfBusy ? 'Fetching…' : 'View stored PDF'}
            </button>
            {pdfUrl && (
              <a className="link" href={pdfUrl} target="_blank" rel="noreferrer">
                Open in a new tab
              </a>
            )}
            {!pdf.share_available && (
              <span className="muted">The reports share is not available here.</span>
            )}
          </div>
          {pdfError && <p className="banner banner-error">{pdfError}</p>}
          {pdfUrl && (
            <iframe className="pdf-frame" src={pdfUrl} title={pdf.referenced_file ?? 'Report'} />
          )}
        </fieldset>
      )}

      {/* `StudyReport.aspx.vb` `BindStudyReport()` (:87): a study whose
          `StudyReport.Id` is 0 is redirected to `New<Modality>Report.aspx` to
          author its first report; only a study that already has one shows the
          stored PDF. 183,884 studies across Echo, TEE, Carotid and Vascular are
          in that state, so this is the ordinary way a report gets written, not
          an edge case.

          Those New* pages are the same forms as the worksheets — identical
          control sets, the only difference being `btnCreateReport` ("Generate")
          where the worksheet has `btnUpdateReport`. So this routes to the
          worksheet, which already inserts the row on first save, and carries
          the legacy's own caption. */}
      {!report.report_id && worksheet && (
        <div className="actions">
          <Link className="link primary" to={worksheet}>
            Generate
          </Link>
          <span className="muted">
            This {open?.study_type ?? 'study'} has no report yet. Generate opens the
            worksheet; saving it writes the report row.
          </span>
        </div>
      )}

      {report.note && open && report.report_id &&
        (worksheet ? (
          <p className="banner banner-warn">
            There is no <code>tblStudyReport</code> row for this{' '}
            {open?.study_type ?? 'study'} study — that table carries the Echo, TEE and
            Carotid reports. This modality keeps its report in its own table, shown on
            the{' '}
            {/* Named for what it points at, not for the button above. Repeating
                that caption put two links with the identical accessible name on
                the page, which a screen reader cannot tell apart. */}
            <Link to={worksheet}>{open?.study_type ?? 'modality'} worksheet</Link>.
          </p>
        ) : (
          <p className="banner banner-warn">{report.note}</p>
        ))}

      {report.report_id && (
        <>
          <fieldset>
            <legend>Signing and file</legend>
            <div className="ro-field">
              <span className="ro-label">Signature status</span>
              <span className="ro-value">
                {report.electronically_signed ? 'Electronically Signed' : 'Not signed'}
                {report.dictated_not_read && ' · Dictated Not Read'}
              </span>
            </div>
            {report.both_captions && (
              <p className="hint hint-warn">
                This report prints <strong>both</strong> captions. Signing does not
                clear <code>DictatedNotRead</code>, which affects 17.4% of signed
                reports.
              </p>
            )}
            <div className="ro-field">
              <span className="ro-label">Signed by</span>
              <span className="ro-value">
                {report.reading_physician || <span className="muted">—</span>}
              </span>
            </div>
            <div className="ro-field">
              <span className="ro-label">Dictation date</span>
              <span className="ro-value">
                {SENTINEL.test(report.dictation_date ?? '') ? (
                  <span className="muted">not dictated</span>
                ) : (
                  (report.dictation_date ?? '').slice(0, 10) || <span className="muted">—</span>
                )}
              </span>
            </div>
            <div className="ro-field">
              <span className="ro-label">PDF</span>
              <span className="ro-value mono">
                {report.referenced_file || <span className="muted">none</span>}
              </span>
              {SENTINEL.test(report.referenced_file ?? '') && (
                <span className="ro-note">
                  Filename embeds the not-transcribed sentinel. Stored names are a
                  frozen snapshot and cannot be recomputed.
                </span>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend>Interpretation</legend>
            {report.interpretation ? (
              <p className="narrative">{report.interpretation}</p>
            ) : (
              <p className="muted">No interpretation text stored.</p>
            )}
          </fieldset>
        </>
      )}

    </main>
  )
}

// A hub button. The legacy hid the control and left the operation reachable;
// here the control is drawn either way and the reason for a refusal is said out
// loud, because "the button is not there" is not an explanation.
function ActionButton({
  caption, allowed, reason, loading, disabled, onClick,
}: {
  caption: string
  allowed: boolean
  reason: string | null
  loading: boolean
  disabled: boolean
  onClick: () => void
}) {
  if (loading) return <span className="muted">{caption}…</span>
  if (!allowed) {
    return (
      <span className="muted" title={reason ?? undefined}>
        {caption} — not available to you
      </span>
    )
  }
  return (
    <button type="button" disabled={disabled} onClick={onClick}>
      {caption}
    </button>
  )
}

// What each action is about to do, in the terms the user cares about, before
// they commit to it. E-Sign also collects the PIN here: it is the legacy's
// `txtPIN` on `ElectronicSignature*.aspx`, matched against the physician's
// passcode, and it is the signature — so it belongs in the confirmation and
// nowhere else.
function Confirm({
  kind, actions, pin, onPin, busy, onCancel, onConfirm,
}: {
  kind: Pending
  actions: ReportActions
  pin: string
  onPin: (v: string) => void
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const needsPin = kind === 'esign' && actions.esign_needs_pin
  const blocked = needsPin && pin.trim() === ''

  return (
    <fieldset className="recipient">
      <legend>{CAPTION[kind]}</legend>

      {kind === 'esign' && (
        <>
          <p>
            Signs the {actions.study_type ?? 'study'} report into{' '}
            <code>{actions.esign_table}</code> under the physician the PIN
            identifies, recording their signature block and signature image.
          </p>
          {actions.esign_page === 'ElectronicSignature.aspx' ? (
            <p className="hint">
              This study type also moves to <strong>Completed</strong> when a reading
              physician signs it.
            </p>
          ) : (
            <p className="hint">
              Records the dictation date. The study status does not change.
            </p>
          )}
          {actions.already_signed && (
            <p className="hint hint-warn">
              This report is already signed. Signing again replaces the physician on
              it.
            </p>
          )}
          {needsPin ? (
            <div className="field">
              <label htmlFor="esign-pin">Physician PIN</label>
              <input
                id="esign-pin"
                type="password"
                autoComplete="off"
                value={pin}
                disabled={busy}
                onChange={(e) => onPin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !blocked && !busy) onConfirm()
                }}
              />
            </div>
          ) : (
            <p className="hint">
              Signing as the physician linked to your account — no PIN required, as
              on the legacy page.
            </p>
          )}
        </>
      )}

      {kind === 'complete' && (
        <p>
          Moves study {actions.study_id} from{' '}
          <strong>{actions.status ?? 'its current status'}</strong> to{' '}
          <strong>Completed</strong>. Nothing else on the study changes.
        </p>
      )}

      {kind === 'refresh' && (
        <>
          <p>
            Re-derives the report's stored header — referring physician, reading
            physician and study location — from the study as it stands now.
          </p>
          <p className="hint">
            The Crystal PDF is not regenerated. The legacy only rewrites the stored
            header for ABI studies; for any other type this reports the drift and
            changes nothing.
          </p>
        </>
      )}

      <div className="actions">
        <button type="button" disabled={busy || blocked} onClick={onConfirm}>
          {busy ? 'Working…' : `Confirm ${CAPTION[kind]}`}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </fieldset>
  )
}

// What actually changed. The legacy re-rendered the page and said nothing, so a
// signature that did not land looked exactly like one that did.
function Outcome({ kind, data }: { kind: Pending; data: ActionResult }) {
  const rows = Object.entries(data.changed)
  const drift = Object.entries(data.drift ?? {})

  if (!data.saved) {
    return (
      <div className="banner banner-warn">
        <strong>Nothing written.</strong> {data.note ?? ''}
        {drift.length > 0 && (
          <ul className="save-summary">
            {drift.map(([column, change]) => (
              <li key={column}>
                {column}: {show(change.stored)} → <strong>{show(change.live)}</strong>{' '}
                <span className="muted">(not applied)</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="banner banner-ok">
      <strong>{CAPTION[kind]} done.</strong>
      {kind === 'esign' && data.physician && ` Signed by ${data.physician}.`}
      {kind === 'esign' && data.completed && ' The study also moved to Completed.'}
      {data.note && ` ${data.note}`}
      <ul className="save-summary">
        {rows.map(([column, change]) => (
          <li key={column}>
            {column === 'status_id' ? 'Status' : column}: {show(change.from)} →{' '}
            <strong>{show(change.to)}</strong>
          </li>
        ))}
      </ul>
      {data.table && (
        <p className="hint muted">
          Written to <code>{data.table}</code>.
        </p>
      )}
    </div>
  )
}

function show(v: string | number | boolean | null | undefined) {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  // Timestamps come back as ISO; the seconds are the useful part of a
  // signature, the microseconds are not.
  const s = String(v)
  return /^\d{4}-\d{2}-\d{2}T/.test(s) ? s.slice(0, 19).replace('T', ' ') : s
}
